// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { useEffect } from 'react';

import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import { gray01, gray04 } from 'presentations/views/styles/color';
import * as tracker from 'presentations/views/helpers/analytics';

interface Props {
  onSubmit: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  useEffect(() => {
    tracker.setEvent('signup calendar notify');
  }, []);

  const onNext = () => {
    tracker.setEvent('signup calendar accept');
    props.onSubmit();
  };

  const onClose = () => {
    tracker.setEvent('signup calendar close');
    props.onClose();
  };

  return (
    <Dialog
      isOpen
      scroll
      width={400}
      disableMinWidth
      titleComponent={
        <TitleContainer>
          <TitleIcon name="calendar-check" color="primary" size="28px" />
          {t('title')}
        </TitleContainer>
      }
      titleBottomMargin={spacing(8)}
      actions={
        <ActionsContainer>
          <StyledButton
            isLoading={props.isLoading}
            color="primary"
            size="large"
            onClick={onNext}
          >
            {t('button')}
          </StyledButton>
        </ActionsContainer>
      }
      onClose={onClose}
    >
      <ImageContainer>
        <StyledImage
          src="pages/signup/recommend_google_scope.png"
          alt={t('message')}
          width={310}
          dpr={2}
        />
      </ImageContainer>
      <Block>
        <Message>{t('message')}</Message>
        <MessageAnnotation>{t('annotation')}</MessageAnnotation>
      </Block>
      <TitleContainer>
        <TitleIcon name="calendar-lock" color="primary" size="28px" />
        {t('privacy message')}
      </TitleContainer>
    </Dialog>
  );
}

const TitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const TitleIcon = styled(Icon)`
  margin-right: ${spacing(8)};
  flex-shrink: 0;
`;

const ImageContainer = styled.div`
  text-align: center;
`;
const StyledImage = styled(Image)`
  height: auto;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;

const Block = styled.div`
  padding-bottom: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  border-bottom: 1px solid ${gray04};
`;
const Message = styled.div`
  margin-top: ${spacing(16)};
  white-space: pre-wrap;
  font-size: ${fontSize(14)};
  font-weight: initial;

  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
    font-size: ${fontSize(12)};
  }
`;
const MessageAnnotation = styled.div`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(12)};
  color: ${gray01};

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;

const ActionsContainer = styled.div`
  text-align: center;
`;
const StyledButton = styled(Button)`
  width: 168px;

  ${mediaMax.tablet} {
    width: 120px;
  }
`;
